import type { BreakPoint } from '@lighthouse/core'
import { DevicePreviewEnum } from '@lighthouse/core'
import { getBrowser, isIpad, isMiniprogram } from '@lighthouse/tools'
import MobileDetect from 'mobile-detect'
import { useWindowSize } from 'react-use'

// ============================================================
// 设备类型（deviceType）与 断点（breakPoint）核心规则
// ============================================================
//
// 【两个核心概念 - 必须分离】
//
// 1. deviceType（设备类型）
//    - 含义：用户使用的真实设备能力
//    - 用途：交互方式（触摸/鼠标）、支付类型（PC扫码/App支付）、UI组件样式
//    - 特点：基于 UserAgent 检测，应用生命周期内不变
//
// 2. breakPointId（断点 ID）
//    - 含义：当前使用的响应式断点
//    - 用途：决定页面/组件的响应式布局样式
//    - 特点：根据场景动态变化
//
// ============================================================
// 管理端（Builder）规则
// ============================================================
// breakPointId: 用户在预览工具栏选择的断点 ID
//   - 决定模拟器盒子尺寸（手机/平板/桌面）
//   - 渲染对应断点的样式配置
//
// deviceType: 跟随当前选中断点的 type 属性
//   - 用于预览时模拟对应设备的 UI 组件样式
//   - 例如：选择手机断点时，表格可能变成列表样式
//
// ============================================================
// 应用端（Runner）规则
// ============================================================
// deviceType: 根据 UserAgent 判断真实设备类型
//   - 小程序环境 → MOBILE
//   - 移动端浏览器 → MOBILE
//   - 桌面端浏览器 → DESKTOP
//
// breakPointId: 根据窗口宽度自动匹配断点列表
//   - 遍历断点列表（按宽度降序）
//   - 返回第一个 width <= 窗口宽度 的断点 ID
//
// ============================================================
// 主断点（mainBreakPoint）
// ============================================================
// 应用的默认/基准断点，用于断点配置合并：
//   - 主断点配置作为 base
//   - 非主断点配置只存储差异值
//   - 渲染时通过 mergeBreakPointConfigure 合并
//
// ============================================================

// ============================================================
// 设备类型检测（基于 UserAgent，用于应用端）
// ============================================================

/**
 * 全局设备类型缓存
 * 避免重复实例化 MobileDetect（每次约 20ms）
 */
let cachedDeviceType: DevicePreviewEnum | null = null

/**
 * 检测真实设备类型（仅在应用初始化时调用一次）
 * 用于判断用户使用的是什么类型的设备（移动端/桌面端）
 *
 * @returns DevicePreviewEnum.MOBILE 或 DevicePreviewEnum.DESKTOP
 */
const detectDeviceType = (): DevicePreviewEnum => {
    // 如果已有缓存，直接返回
    if (cachedDeviceType !== null) {
        return cachedDeviceType
    }

    // 小程序环境直接判定为移动端
    if (isMiniprogram()) {
        cachedDeviceType = DevicePreviewEnum.MOBILE
        return cachedDeviceType
    }

    const browser = getBrowser()
    // iPadOS 13+ UA 会伪装成 Mac
    if (isIpad(browser)) {
        cachedDeviceType = DevicePreviewEnum.MOBILE
        return cachedDeviceType
    }

    // 通过 UserAgent 判断设备类型
    if (browser.userAgent) {
        const md = new MobileDetect(browser.userAgent)
        const isMobile = md.mobile() || md.tablet()
        cachedDeviceType = isMobile ? DevicePreviewEnum.MOBILE : DevicePreviewEnum.DESKTOP
        return cachedDeviceType
    }

    // 默认桌面端
    cachedDeviceType = DevicePreviewEnum.DESKTOP
    return cachedDeviceType
}

/**
 * 获取缓存的设备类型
 * 用于纯函数中获取设备类型（如支付类型判断）
 *
 * @returns DevicePreviewEnum
 */
export const getDeviceTypeFromCache = (): DevicePreviewEnum => {
    if (cachedDeviceType === null) {
        // 如果还没初始化，进行一次检测并缓存
        return detectDeviceType()
    }
    return cachedDeviceType
}

/**
 * 判断是否是移动设备（基于缓存）
 * 用于判断交互方式（触摸 vs 鼠标）等
 *
 * @returns boolean
 */
export const isMobileDevice = (): boolean => {
    return getDeviceTypeFromCache() === DevicePreviewEnum.MOBILE
}

// ============================================================
// 断点尺寸检测（基于窗口宽度，用于响应式 UI 布局）
// ============================================================

/**
 * Hook: 根据窗口宽度获取当前断点 ID
 * 用于响应式 UI 布局，如分页器大小、列数等
 *
 * @param breakPoint 断点配置数组
 * @returns 当前匹配的断点 ID
 */
export const useBreakPointId = (breakPoint: BreakPoint[]): string | undefined => {
    const { width } = useWindowSize()

    const list = breakPoint.sort((item1, item2) => item2.width - item1.width)

    for (const element of list) {
        if (width >= element.width) {
            return element.id
        }
    }

    return list[list.length - 1]?.id
}
